import React, { useEffect, useState } from 'react'
import 'react-circular-progressbar/dist/styles.css';
import { Card, Container, Row, Col, Button, Table, Form, Badge, Modal, Spinner } from 'react-bootstrap';
import { useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DashboardService } from '../../../service/dashboard-service';
import { successToast, warningToast } from '../../../constants/Toasts-Components';
import Chart from 'react-apexcharts';

const REPORT_ALLOWED_EMAIL = 'xheni_metani@hotmail.com';

// Date preset options
const DATE_PRESETS = [
    { label: 'Ultima Settimana', days: 7 },
    { label: 'Ultimo Mese', days: 30 },
    { label: 'Ultimi 3 Mesi', days: 90 },
    { label: 'Ultimi 6 Mesi', days: 180 },
    { label: 'Ultimo Anno', days: 365 },
];

// Stat Box Component
const StatBox = ({ icon, iconBg, title, value, color }) => {
    const formatValue = (val) => {
        if (typeof val === 'number') {
            if (val >= 1000000) {
                return `€${(val / 1000000).toFixed(1)}M`;
            } else if (val >= 1000) {
                return val.toLocaleString('it-IT');
            }
            return val.toString();
        }
        return val;
    };

    return (
        <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                    <div 
                        className="d-flex align-items-center justify-content-center rounded" 
                        style={{ 
                            width: '48px', 
                            height: '48px', 
                            backgroundColor: iconBg,
                        }}
                    >
                        <i className={icon} style={{ fontSize: '24px', color: '#fff' }}></i>
                    </div>
                </div>
                <div className="mt-3">
                    <p className="text-muted mb-1 small">{title}</p>
                    <h3 className="mb-0 font-weight-bold" style={{ color }}>{formatValue(value)}</h3>
                </div>
            </Card.Body>
        </Card>
    );
};

// Filter Tag Component
const FilterTag = ({ label, options, value, onChange, nameKey = 'name', idKey = 'id' }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (id) => {
        if (!value.some(v => String(v) === String(id))) {
            onChange([...value, id]);
        }
        setIsOpen(false);
    };

    const handleRemove = (e, idToRemove) => {
        e.stopPropagation();
        onChange(value.filter(id => String(id) !== String(idToRemove)));
    };

    const getName = (id) => {
        const opt = options.find(o => String(o[idKey]) === String(id));
        return opt ? (opt[nameKey] || opt.email || `${opt.name} ${opt.surname || ''}`.trim() || id) : id;
    };

    const availableOptions = options.filter(opt => !value.some(v => String(v) === String(opt[idKey])));

    return (
        <div className="position-relative" style={{ minWidth: '140px' }}>
            <div 
                className="border rounded px-2 py-1 bg-white d-flex align-items-center cursor-pointer"
                style={{ minHeight: '36px', cursor: 'pointer', fontSize: '0.85rem' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-muted mr-1">{label}:</span>
                <div className="d-flex flex-wrap align-items-center flex-grow-1" style={{ gap: '4px' }}>
                    {value.length === 0 ? (
                        <span className="text-muted">Tutti</span>
                    ) : (
                        value.map(id => (
                            <Badge 
                                key={id} 
                                variant="primary" 
                                className="d-inline-flex align-items-center px-2 py-1"
                                style={{ fontSize: '0.75rem' }}
                            >
                                {getName(id)}
                                <span 
                                    onClick={(e) => handleRemove(e, id)}
                                    className="ml-1"
                                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    ×
                                </span>
                            </Badge>
                        ))
                    )}
                </div>
                <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line ml-1`}></i>
            </div>
            {isOpen && availableOptions.length > 0 && (
                <div 
                    className="position-absolute bg-white border rounded shadow-sm mt-1 w-100"
                    style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                >
                    {availableOptions.map(opt => (
                        <div 
                            key={opt[idKey]} 
                            className="px-3 py-2 hover-bg-light"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelect(opt[idKey])}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            {opt[nameKey] || opt.email || `${opt.name} ${opt.surname || ''}`.trim()}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Statistics Table Component
const StatsTable = ({ revenueByState, onCountClick }) => {
    const formatCurrency = (value) => {
        if (!value) return '€0,00';
        return `€${Number(value).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatNumber = (value) => {
        if (!value) return '0';
        return Number(value).toLocaleString('it-IT');
    };

    const data = [
        {
            label: 'In Lavorazione',
            stateKey: 'processing',
            count: revenueByState.counts[0] || 0,
            amount: revenueByState.amounts[0] || 0,
            commission: revenueByState.commissions[0] || 0,
            color: '#F59E0B'
        },
        {
            label: 'Liquidate',
            stateKey: 'liquidated',
            count: revenueByState.counts[1] || 0,
            amount: revenueByState.amounts[1] || 0,
            commission: revenueByState.commissions[1] || 0,
            color: '#10B981'
        },
        {
            label: 'Annullate',
            stateKey: 'cancelled',
            count: revenueByState.counts[2] || 0,
            amount: revenueByState.amounts[2] || 0,
            commission: revenueByState.commissions[2] || 0,
            color: '#EF4444'
        }
    ];

    const renderCount = (count, stateKey, label) => (
        <button
            type="button"
            className="btn btn-link p-0"
            style={{ fontWeight: 'inherit', fontSize: '1rem', textDecoration: 'underline' }}
            title={stateKey === 'all' ? 'Visualizza tutte le pratiche' : `Visualizza le pratiche ${label}`}
            onClick={() => onCountClick(stateKey, label)}
        >
            {formatNumber(count)}
        </button>
    );

    return (
        <Card className="shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 font-weight-bold">Riepilogo per Stato</h5>
            </Card.Header>
            <Card.Body className="p-0">
                <Table responsive className="mb-0" style={{ fontSize: '0.9rem' }}>
                    <thead className="bg-light">
                        <tr>
                            <th className="py-3 px-4" style={{ fontWeight: '600' }}>Stato</th>
                            <th className="py-3 px-4 text-center" style={{ fontWeight: '600' }}>N° Opportunità</th>
                            <th className="py-3 px-4 text-right" style={{ fontWeight: '600' }}>Montante</th>
                            <th className="py-3 px-4 text-right" style={{ fontWeight: '600' }}>Commissioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index} style={{ borderBottom: index < data.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                <td className="py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            style={{ 
                                                width: '12px', 
                                                height: '12px', 
                                                borderRadius: '50%', 
                                                backgroundColor: row.color,
                                                marginRight: '10px'
                                            }}
                                        ></div>
                                        <span style={{ fontWeight: '500' }}>{row.label}</span>
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-center" style={{ fontWeight: '600', fontSize: '1rem' }}>
                                    {renderCount(row.count, row.stateKey, row.label)}
                                </td>
                                <td className="py-3 px-4 text-right" style={{ fontWeight: '500' }}>
                                    {formatCurrency(row.amount)}
                                </td>
                                <td className="py-3 px-4 text-right" style={{ fontWeight: '500' }}>
                                    {formatCurrency(row.commission)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-light">
                        <tr style={{ borderTop: '2px solid #dee2e6' }}>
                            <td className="py-3 px-4" style={{ fontWeight: '700' }}>Totale</td>
                            <td className="py-3 px-4 text-center" style={{ fontWeight: '700', fontSize: '1rem' }}>
                                {renderCount(data.reduce((sum, row) => sum + row.count, 0), 'all', 'Totale')}
                            </td>
                            <td className="py-3 px-4 text-right" style={{ fontWeight: '700' }}>
                                {formatCurrency(data.reduce((sum, row) => sum + row.amount, 0))}
                            </td>
                            <td className="py-3 px-4 text-right" style={{ fontWeight: '700' }}>
                                {formatCurrency(data.reduce((sum, row) => sum + row.commission, 0))}
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </Card.Body>
        </Card>
    );
};

const Dashbord = () => {
    const history = useHistory();
    const { config } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [downloadingReport, setDownloadingReport] = useState(false);
    
    // Stats for top boxes
    const [stats, setStats] = useState({
        new_contacts: 0,
        new_opportunities: 0,
        opportunities_liquidated: 0,
        total_revenue: 0
    });
    
    // Graph data
    const [countList, setCountList] = useState([]);
    const [amountList, setAmountList] = useState([]);
    const [commissionList, setCommissionList] = useState([]);
    const [liquidatedAmountList, setLiquidatedAmountList] = useState([]);
    const [liquidatedCommissionList, setLiquidatedCommissionList] = useState([]);
    const [closedPositiveList, setClosedPositiveList] = useState([]);
    const [closedNegativeList, setClosedNegativeList] = useState([]);

    // Revenue by state data
    const [revenueByState, setRevenueByState] = useState({
        categories: ['In Lavorazione', 'Liquidate', 'Annullate'],
        amounts: [0, 0, 0],
        commissions: [0, 0, 0],
        counts: [0, 0, 0]
    });
    
    // Default dates: End = Today, Start = 7 days ago
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [activePreset, setActivePreset] = useState(7);

    // Modal states
    const [showOpportunitiesModal, setShowOpportunitiesModal] = useState(false);
    const [selectedDateOpportunities, setSelectedDateOpportunities] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [modalChartType, setModalChartType] = useState(''); // 'count' or 'closed'

    const [showStateModal, setShowStateModal] = useState(false);
    const [stateModalLoading, setStateModalLoading] = useState(false);
    const [stateModalLabel, setStateModalLabel] = useState('');
    const [stateOpportunities, setStateOpportunities] = useState([]);

    // Filter states
    const [dropdownOptions, setDropdownOptions] = useState({
        agencies: [],
        agents: [],
        banks: [],
        products: [],
        campaigns: []
    });
    
    const [selectedFilters, setSelectedFilters] = useState({
        agency_id: [],
        agent_id: [],
        bank_id: [],
        product_id: [],
        campaign_id: []
    });

    useEffect(() => {
        loadDropdownOptions();
        setLoading(false);
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadDashboardData(controller.signal);
        return () => controller.abort();
    }, [startDate, endDate, selectedFilters]);

    const handleDatePreset = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
        setActivePreset(days);
    };

    const handleFilterChange = (field, newValue) => {
        let updatedFilters = { ...selectedFilters, [field]: newValue };
        
        // If agencies are changed, filter out agents that don't belong to selected agencies
        if (field === 'agency_id') {
            if (newValue.length > 0) {
                // Filter existing selected agents to keep only those from selected agencies
                const validAgents = updatedFilters.agent_id.filter(agentEmail => {
                    const agent = dropdownOptions.agents.find(a => String(a.email) === String(agentEmail));
                    return agent && newValue.some(agencyId => String(agent.agency__id) === String(agencyId));
                });
                updatedFilters.agent_id = validAgents;
            }
            // If no agencies selected, keep all selected agents (no filtering)
        }
        
        setSelectedFilters(updatedFilters);
    };

    const handleBarClick = async (event, chartContext, { dataPointIndex, seriesIndex }) => {
        const dates = getDatesInRange(startDate, endDate);
        const clickedDate = dates[dataPointIndex];
        
        setSelectedDate(clickedDate);
        setModalLoading(true);
        setShowOpportunitiesModal(true);
        
        // Determine chart type based on the chart that was clicked
        const chartType = seriesIndex !== undefined && seriesIndex >= 0 ? 'closed' : 'count';
        setModalChartType(chartType);
        
        try {
            const service = new DashboardService();
            const params = { 
                date: clickedDate,
                ...selectedFilters
            };
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || (Array.isArray(params[key]) && params[key].length === 0)) {
                    delete params[key];
                }
            });
            
            const response = await service.getOpportunitiesByDate(clickedDate, params);
            setSelectedDateOpportunities(response.opportunities || []);
        } catch (error) {
            console.error("Error loading opportunities for date", error);
            setSelectedDateOpportunities([]);
        } finally {
            setModalLoading(false);
        }
    };

    const openContactInNewTab = (contactId) => {
        if (!contactId) return;
        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        window.open(`${base}/contact-view/${contactId}`, '_blank', 'noopener,noreferrer');
    };

    const handleStateCountClick = async (stateCategory, label) => {
        setStateModalLabel(label);
        setStateModalLoading(true);
        setShowStateModal(true);
        setStateOpportunities([]);

        try {
            const service = new DashboardService();
            const params = {
                start_date: startDate,
                end_date: endDate,
                state_category: stateCategory,
                ...selectedFilters
            };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || (Array.isArray(params[key]) && params[key].length === 0)) {
                    delete params[key];
                }
            });

            const response = await service.getOpportunitiesByState(params);
            setStateOpportunities(response.opportunities || []);
        } catch (error) {
            console.error("Error loading opportunities by state", error);
            setStateOpportunities([]);
        } finally {
            setStateModalLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        setDownloadingReport(true);
        try {
            const service = new DashboardService();
            const params = {
                start_date: startDate,
                end_date: endDate,
                ...selectedFilters
            };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || (Array.isArray(params[key]) && params[key].length === 0)) {
                    delete params[key];
                }
            });

            const response = await service.downloadDashboardReport(params);
            const blob = new Blob(
                [response.data],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_${startDate}_${endDate}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            successToast('Report scaricato');
        } catch (error) {
            console.error('Error downloading dashboard report', error);
            warningToast('Impossibile scaricare il report');
        } finally {
            setDownloadingReport(false);
        }
    };

    const loadDropdownOptions = async () => {
        try {
            const service = new DashboardService();
            const response = await service.getDropdownOptions();
            setDropdownOptions({
                agencies: response.agencies || [],
                agents: response.agents || [],
                banks: response.banks || response.banca || [],
                products: response.products || [],
                campaigns: response.campaigns || []
            });
        } catch (error) {
            console.error("Error loading dropdown options", error);
        }
    };

    const loadDashboardData = async (signal) => {
        try {
            const service = new DashboardService();
            const params = { 
                start_date: startDate, 
                end_date: endDate,
                ...selectedFilters
            };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || (Array.isArray(params[key]) && params[key].length === 0)) {
                    delete params[key];
                }
            });

            const response = await service.getDashboardSummary(params, signal);
            const series = response.series || {};
            setStats(response.stats || {});
            setCountList(series.count || []);
            setAmountList(series.amount || []);
            setCommissionList(series.commission || []);
            setLiquidatedAmountList(series.liquidated_amount || []);
            setLiquidatedCommissionList(series.liquidated_commission || []);
            setClosedPositiveList(series.closed_positive || []);
            setClosedNegativeList(series.closed_negative || []);
            setRevenueByState(response.revenue_by_state || {
                categories: ['In Lavorazione', 'Liquidate', 'Annullate'],
                amounts: [0, 0, 0],
                commissions: [0, 0, 0],
                counts: [0, 0, 0]
            });
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            console.error("Error loading dashboard data", error);
        }
    };

    const getDatesInRange = (startDate, endDate) => {
        const date = new Date(startDate);
        const end = new Date(endDate);
        const dates = [];
        while (date <= end) {
            dates.push(new Date(date).toISOString().split('T')[0]);
            date.setDate(date.getDate() + 1);
        }
        return dates;
    };

    const itLocale = {
        "name": "it",
        "options": {
            "months": ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
            "shortMonths": ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
            "days": ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"],
            "shortDays": ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
            "toolbar": {
                "exportToSVG": "Scarica SVG",
                "exportToPNG": "Scarica PNG",
                "exportToCSV": "Scarica CSV",
                "menu": "Menu",
                "selection": "Selezione",
                "selectionZoom": "Zoom Selezione",
                "zoomIn": "Zoom In",
                "zoomOut": "Zoom Out",
                "pan": "Pan",
                "reset": "Reset Zoom"
            }
        }
    };

    const getBaseChartOptions = (title) => ({
        chart: {
            zoom: { enabled: false },
            toolbar: { show: false },
            locales: [itLocale],
            defaultLocale: 'it'
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: {
            categories: getDatesInRange(startDate, endDate),
            type: 'datetime',
            labels: {
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM \'yy',
                    day: 'dd MMM',
                    hour: 'HH:mm'
                }
            }
        },
        yaxis: {
            title: { text: title },
            labels: { formatter: (value) => value.toFixed(0) }
        },
        grid: {
            row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 }
        },
        legend: { position: 'top' }
    });

    // Bar chart options for practices created
    const countChartOptions = {
        ...getBaseChartOptions('Numero Pratiche'),
        chart: { 
            ...getBaseChartOptions('').chart, 
            type: 'bar',
            events: {
                dataPointSelection: handleBarClick
            }
        },
        colors: ['#008FFB'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } }
    };

    // Line chart options for commissions (dual line)
    const commissionChartOptions = {
        ...getBaseChartOptions('Commissioni (€)'),
        chart: { ...getBaseChartOptions('').chart, type: 'line' },
        colors: ['#775DD0', '#00E396'],
        stroke: { curve: 'smooth', width: [3, 3] }
    };

    // Line chart options for amounts (dual line)
    const amountChartOptions = {
        ...getBaseChartOptions('Montante (€)'),
        chart: { ...getBaseChartOptions('').chart, type: 'line' },
        colors: ['#FF4560', '#00E396'],
        stroke: { curve: 'smooth', width: [3, 3] }
    };

    // Double bar chart options for closed practices
    const closedChartOptions = {
        ...getBaseChartOptions('Pratiche Chiuse'),
        chart: { 
            ...getBaseChartOptions('').chart, 
            type: 'bar', 
            stacked: false,
            events: {
                dataPointSelection: handleBarClick
            }
        },
        colors: ['#00E396', '#FF4560'],
        plotOptions: { 
            bar: { 
                borderRadius: 4, 
                columnWidth: '60%',
                dataLabels: { position: 'top' }
            } 
        }
    };

    const countChartSeries = [{ name: "Pratiche Create", data: countList }];
    
    const commissionChartSeries = [
        { name: "Commissioni Totali", data: commissionList },
        { name: "Commissioni Liquidate", data: liquidatedCommissionList }
    ];
    
    const amountChartSeries = [
        { name: "Montante Totale", data: amountList },
        { name: "Montante Liquidato", data: liquidatedAmountList }
    ];
    
    const closedChartSeries = [
        { name: "Liquidate", data: closedPositiveList },
        { name: "Annullate", data: closedNegativeList }
    ];

    // Revenue by state - Grouped bar chart
    const revenueByStateChartOptions = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            locales: [itLocale],
            defaultLocale: 'it'
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 6,
                dataLabels: { position: 'top' }
            }
        },
        dataLabels: { 
            enabled: true,
            formatter: (val) => val >= 1000 ? `€${(val / 1000).toFixed(0)}k` : `€${val.toFixed(0)}`,
            offsetY: -20,
            style: { fontSize: '11px', colors: ['#304758'] }
        },
        colors: ['#F59E0B', '#8B5CF6'],
        xaxis: {
            categories: revenueByState.categories || ['In Lavorazione', 'Liquidate', 'Annullate'],
        },
        yaxis: {
            title: { text: 'Importo (€)' },
            labels: {
                formatter: (val) => val >= 1000 ? `€${(val / 1000).toFixed(0)}k` : `€${val.toFixed(0)}`
            }
        },
        legend: { position: 'top' },
        grid: {
            row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 }
        },
        tooltip: {
            y: {
                formatter: (val) => `€${val.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            }
        }
    };

    const revenueByStateChartSeries = [
        { name: 'Montante', data: revenueByState.amounts || [0, 0, 0] },
        { name: 'Provvigioni', data: revenueByState.commissions || [0, 0, 0] }
    ];

    // Opportunity distribution donut chart
    const totalOpportunities = (revenueByState.counts || [0, 0, 0]).reduce((a, b) => a + b, 0);
    const opportunityDistributionOptions = {
        chart: {
            type: 'donut',
            toolbar: { show: false },
        },
        labels: revenueByState.categories || ['In Lavorazione', 'Liquidate', 'Annullate'],
        colors: ['#F59E0B', '#10B981', '#EF4444'],
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        name: { show: true, fontSize: '14px' },
                        value: { 
                            show: true, 
                            fontSize: '20px', 
                            fontWeight: 'bold',
                            formatter: (val) => val
                        },
                        total: {
                            show: true,
                            label: 'Totale',
                            fontSize: '13px',
                            formatter: () => totalOpportunities
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val.toFixed(1)}%`,
            style: { fontSize: '12px' }
        },
        legend: { position: 'bottom', fontSize: '13px' },
        tooltip: {
            y: {
                formatter: (val) => `${val} opportunità`
            }
        }
    };

    const opportunityDistributionSeries = revenueByState.counts || [0, 0, 0];

    // Filter agents based on selected agencies
    const canDownloadReport = !!(
        config?.is_admin ||
        (config?.email || '').trim().toLowerCase() === REPORT_ALLOWED_EMAIL
    );

    const filteredAgents = selectedFilters.agency_id.length > 0 
        ? dropdownOptions.agents.filter(agent => 
            selectedFilters.agency_id.some(agencyId => String(agent.agency__id) === String(agencyId))
          )
        : dropdownOptions.agents;

    return (
        <Container fluid>
            {/* Header */}
            <Row className="mb-4">
                <Col className='d-flex flex-row justify-content-between align-items-center'>
                    <h2 className="font-weight-bold mb-0">Dashboard</h2>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        {canDownloadReport && (
                            <Button
                                variant="outline-success"
                                onClick={handleDownloadReport}
                                disabled={downloadingReport}
                                className="px-4"
                            >
                                <i className="ri-file-excel-2-line mr-1"></i>
                                {downloadingReport ? 'Download...' : 'Scarica Report'}
                            </Button>
                        )}
                        <Button variant="primary" onClick={loadDashboardData} className="px-4">
                            <i className="ri-refresh-line mr-1"></i> Aggiorna Dati
                        </Button>
                    </div>
                </Col>
            </Row>
            
            {/* Stats Boxes */}
            <Row className="mb-4">
                <Col md={3} sm={6} className="mb-3">
                    <StatBox 
                        icon="ri-contacts-line" 
                        iconBg="#3B82F6"
                        title="Nuovi Contatti"
                        value={stats.new_contacts}
                        color="#3B82F6"
                    />
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <StatBox 
                        icon="ri-line-chart-line" 
                        iconBg="#10B981"
                        title="Nuove Opportunità"
                        value={stats.new_opportunities}
                        color="#10B981"
                    />
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <StatBox 
                        icon="ri-checkbox-circle-line" 
                        iconBg="#8B5CF6"
                        title="Opportunità Liquidate"
                        value={stats.opportunities_liquidated}
                        color="#8B5CF6"
                    />
                </Col>
                <Col md={3} sm={6} className="mb-3">
                    <StatBox 
                        icon="ri-money-euro-circle-line" 
                        iconBg="#F59E0B"
                        title="Ricavi Totali"
                        value={stats.total_revenue}
                        color="#F59E0B"
                    />
                </Col>
            </Row>

            {/* Filters Bar */}
            <Card className="shadow-sm mb-4">
                <Card.Body className="py-3">
                    {/* Date Presets */}
                    <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: '8px' }}>
                        
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <Form.Control 
                                type="date" 
                                value={startDate} 
                                onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
                                size="sm"
                                style={{ width: '140px' }}
                            />
                            <span>-</span>
                            <Form.Control 
                                type="date" 
                                value={endDate} 
                                onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
                                size="sm"
                                style={{ width: '140px' }}
                            />
                            {DATE_PRESETS.map((preset) => (
                            <Badge 
                                key={preset.days}
                                variant={activePreset === preset.days ? "primary" : "light"}
                                className="px-1 py-1"
                                style={{ 
                                    cursor: 'pointer', 
                                    fontSize: '0.7rem',
                                    fontWeight: activePreset === preset.days ? '600' : '400',
                                    border: activePreset !== preset.days ? '1px solid #dee2e6' : 'none'
                                }}
                                onClick={() => handleDatePreset(preset.days)}
                            >
                                {preset.label}
                            </Badge>
                        ))}
                        </div>
                    </div>

                    {/* Filter Tags */}
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '12px' }}>
                        {!config.is_agent && !config.is_agency_boss && (
                            <FilterTag 
                                label="Agenzia"
                                options={dropdownOptions.agencies}
                                value={selectedFilters.agency_id}
                                onChange={(val) => handleFilterChange('agency_id', val)}
                            />
                        )}

                        {!config.is_agent && (
                            <FilterTag 
                                label="Agente"
                                options={filteredAgents}
                                value={selectedFilters.agent_id}
                                onChange={(val) => handleFilterChange('agent_id', val)}
                                idKey="email"
                            />
                        )}

                        <FilterTag 
                            label="Banca"
                            options={dropdownOptions.banks}
                            value={selectedFilters.bank_id}
                            onChange={(val) => handleFilterChange('bank_id', val)}
                            idKey="id"
                        />

                        <FilterTag 
                            label="Prodotto"
                            options={dropdownOptions.products}
                            value={selectedFilters.product_id}
                            onChange={(val) => handleFilterChange('product_id', val)}
                        />

                        <FilterTag 
                            label="Campagna"
                            options={dropdownOptions.campaigns}
                            value={selectedFilters.campaign_id}
                            onChange={(val) => handleFilterChange('campaign_id', val)}
                        />

                        {Object.values(selectedFilters).some(arr => arr.length > 0) && (
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => setSelectedFilters({
                                    agency_id: [],
                                    agent_id: [],
                                    bank_id: [],
                                    product_id: [],
                                    campaign_id: []
                                })}
                            >
                                <i className="ri-close-line mr-1"></i>
                                Pulisci Filtri
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Statistics Table */}
            <Row className="mb-4">
                <Col lg={12}>
                    <StatsTable revenueByState={revenueByState} onCountClick={handleStateCountClick} />
                </Col>
            </Row>

            {/* Graphs Section */}
            <Row>
                {/* Practices Created - Bar Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Pratiche Create</h5>
                        </Card.Header>
                        <Card.Body>
                            <Chart 
                                options={countChartOptions} 
                                series={countChartSeries} 
                                type="bar" 
                                height={300} 
                            />
                        </Card.Body>
                    </Card>
                </Col>

                {/* Closed Practices - Double Bar Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Pratiche Chiuse</h5>
                        </Card.Header>
                        <Card.Body>
                            <Chart 
                                options={closedChartOptions} 
                                series={closedChartSeries} 
                                type="bar" 
                                height={300} 
                            />
                        </Card.Body>
                    </Card>
                </Col>

                {/* Commissions - Dual Line Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Commissioni</h5>
                        </Card.Header>
                        <Card.Body>
                            <Chart 
                                options={commissionChartOptions} 
                                series={commissionChartSeries} 
                                type="line" 
                                height={300} 
                            />
                        </Card.Body>
                    </Card>
                </Col>

                {/* Amounts - Dual Line Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Montante</h5>
                        </Card.Header>
                        <Card.Body>
                            <Chart 
                                options={amountChartOptions} 
                                series={amountChartSeries} 
                                type="line" 
                                height={300} 
                            />
                        </Card.Body>
                    </Card>
                </Col>

                {/* Revenue by State - Grouped Bar Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Ricavi per Stato Opportunità</h5>
                        </Card.Header>
                        <Card.Body>
                            <Chart 
                                options={revenueByStateChartOptions} 
                                series={revenueByStateChartSeries} 
                                type="bar" 
                                height={300} 
                            />
                        </Card.Body>
                    </Card>
                </Col>

                {/* Opportunity Distribution - Donut Chart */}
                <Col md={6} className="mb-4">
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">Distribuzione Opportunità</h5>
                        </Card.Header>
                        <Card.Body className="d-flex align-items-center justify-content-center">
                            {totalOpportunities > 0 ? (
                                <Chart 
                                    options={opportunityDistributionOptions} 
                                    series={opportunityDistributionSeries} 
                                    type="donut" 
                                    height={300} 
                                    width="100%"
                                />
                            ) : (
                                <div className="text-center py-4">
                                    <i className="ri-pie-chart-line" style={{ fontSize: '48px', color: '#dee2e6' }}></i>
                                    <p className="text-muted mt-2">Nessun dato disponibile</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            
            <Modal show={showStateModal} onHide={() => setShowStateModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-file-list-3-line mr-2"></i>
                        Pratiche — {stateModalLabel}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {stateModalLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Caricamento pratiche...</p>
                        </div>
                    ) : stateOpportunities.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="ri-inbox-line" style={{ fontSize: '48px', color: '#dee2e6' }}></i>
                            <p className="text-muted mt-2">Nessuna pratica trovata per questo stato</p>
                        </div>
                    ) : (
                        <Table responsive hover size="sm">
                            <thead className="bg-light">
                                <tr>
                                    <th>Contatto</th>
                                    <th>Telefono</th>
                                    <th>Montante</th>
                                    <th>Provvigione %</th>
                                    <th>Commissione</th>
                                    <th>Stato</th>
                                    <th>Prodotto</th>
                                    <th>Banca</th>
                                    <th>Agente</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stateOpportunities.map(opp => (
                                    <tr
                                        key={opp.id}
                                        onClick={() => openContactInNewTab(opp.contact_id)}
                                        title={opp.contact_id ? 'Apri contatto in una nuova scheda' : ''}
                                        style={{ cursor: opp.contact_id ? 'pointer' : 'default' }}
                                    >
                                        <td>
                                            <strong>{opp.contact_name}</strong>
                                        </td>
                                        <td>{opp.contact_phone}</td>
                                        <td>€{opp.importo_richiesto.toLocaleString('it-IT')}</td>
                                        <td>{opp.provvigione}%</td>
                                        <td>€{opp.commission_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                                        <td>
                                            <Badge
                                                variant={opp.is_liquidated ? 'success' : opp.is_cancelled ? 'danger' : 'warning'}
                                                className="px-2 py-1"
                                            >
                                                {opp.state_name}
                                            </Badge>
                                        </td>
                                        <td>{opp.product_name}</td>
                                        <td>{opp.banca}</td>
                                        <td>{opp.agent_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <div className="text-muted">
                            <small>
                                Totale: <strong>{stateOpportunities.length}</strong> pratiche
                                {stateOpportunities.length > 0 && (
                                    <>
                                        {' • '}
                                        Clicca una riga per aprire il contatto in una nuova scheda
                                    </>
                                )}
                            </small>
                        </div>
                        <Button variant="secondary" onClick={() => setShowStateModal(false)}>
                            Chiudi
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Opportunities Modal */}
            <Modal show={showOpportunitiesModal} onHide={() => setShowOpportunitiesModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-calendar-line mr-2"></i>
                        Opportunità del {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('it-IT') : ''}
                        {modalChartType === 'closed' && ' - Pratiche Chiuse'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {modalLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Caricamento opportunità...</p>
                        </div>
                    ) : selectedDateOpportunities.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="ri-inbox-line" style={{ fontSize: '48px', color: '#dee2e6' }}></i>
                            <p className="text-muted mt-2">Nessuna opportunità trovata per questa data</p>
                        </div>
                    ) : (
                        <Table responsive hover size="sm">
                            <thead className="bg-light">
                                <tr>
                                    <th>Contatto</th>
                                    <th>Telefono</th>
                                    <th>Montante</th>
                                    <th>Provvigione %</th>
                                    <th>Commissione</th>
                                    <th>Stato</th>
                                    <th>Prodotto</th>
                                    <th>Banca</th>
                                    <th>Agente</th>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedDateOpportunities.map(opp => (
                                    <tr key={opp.id}>
                                        <td>
                                            <strong>{opp.contact_name}</strong>
                                        </td>
                                        <td>{opp.contact_phone}</td>
                                        <td>€{opp.importo_richiesto.toLocaleString('it-IT')}</td>
                                        <td>{opp.provvigione}%</td>
                                        <td>€{opp.commission_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                                        <td>
                                            <Badge 
                                                variant={opp.is_liquidated ? 'success' : opp.is_cancelled ? 'danger' : 'warning'}
                                                className="px-2 py-1"
                                            >
                                                {opp.state_name}
                                            </Badge>
                                        </td>
                                        <td>{opp.product_name}</td>
                                        <td>{opp.banca}</td>
                                        <td>{opp.agent_name}</td>
                                        <td>
                                            {opp.contact_id && (
                                                <Link 
                                                    to={`/contact-view/${opp.contact_id}`} 
                                                    className="btn btn-sm btn-outline-primary mr-1"
                                                    title="Visualizza Contatto"
                                                >
                                                    <i className="ri-eye-line"></i>
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <div className="text-muted">
                            <small>
                                Totale: <strong>{selectedDateOpportunities.length}</strong> opportunità
                                {selectedDateOpportunities.length > 0 && (
                                    <>
                                        {' • '}
                                        Montante totale: <strong>€{selectedDateOpportunities.reduce((sum, opp) => sum + opp.importo_richiesto, 0).toLocaleString('it-IT')}</strong>
                                        {' • '}
                                        Commissioni totali: <strong>€{selectedDateOpportunities.reduce((sum, opp) => sum + opp.commission_amount, 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</strong>
                                    </>
                                )}
                            </small>
                        </div>
                        <Button variant="secondary" onClick={() => setShowOpportunitiesModal(false)}>
                            Chiudi
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default Dashbord;